import { Button } from "@svea/design-system-components-react";
import { ButtonGroup, Stack, useText } from "@sveabank/cc-common";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { usePageContext } from "../../../../hooks/context";
import { useCreateClcPaymentOrderMutation } from "../../../../services/clc-application";
import type { ClcPaymentConfig } from "../../../../types/clc-application";
import { useClcCompanyApplication } from "../../hooks";
import { ClcPaymentSuccessModal } from "./success-payment-modal";

type Props = {
	onCancel: () => void;
};

const currentUrl = window.location.origin + window.location.pathname;

const paymentConfig: ClcPaymentConfig = {
	checkoutUri: currentUrl,
	confirmationUri: `${currentUrl}?clcPaymentSuccess=true`,
	termsUri: `${currentUrl}/new-company/terms`,
};

const ClcPayment = ({ onCancel }: Props) => {
	const [t, texts] = useText();
	const {
		clcApplicationDetails: { status },
		applicationId,
	} = usePageContext(useClcCompanyApplication);
	const [createClcPaymentOrder] = useCreateClcPaymentOrderMutation();
	const [queryParams] = useSearchParams();
	const hasPaymentCompletedParam = queryParams.has("clcPaymentSuccess", "true");

	const divRef = useRef<HTMLDivElement>(null);
	const [checkoutSnippet, setCheckoutOrderSnippet] = useState<string>("");

	const [isPaymentSuccessModalOpen, setIsPaymentSuccessModalOpen] =
		useState(hasPaymentCompletedParam);

	const loadCheckoutOrder = useCallback(async () => {
		if (status !== "AwaitingPayment") return;
		const order = await createClcPaymentOrder({
			applicationId,
			config: paymentConfig,
		}).unwrap();
		if (order?.gui?.snippet) {
			setCheckoutOrderSnippet(order.gui.snippet);
		}
	}, [setCheckoutOrderSnippet, status]);

	useEffect(() => {
		loadCheckoutOrder();
	}, [loadCheckoutOrder]);

	useEffect(() => {
		if (status === "PaymentCompleted") setIsPaymentSuccessModalOpen(true);
	}, [status]);

	// Since a custom script needs to be executed we must add the snippet via contextual fragment
	useEffect(() => {
		if (status !== "AwaitingPayment") return;
		const fragment = document.createRange().createContextualFragment(checkoutSnippet);
		divRef.current!.replaceChildren(fragment);
	}, [checkoutSnippet]);

	return (
		<>
			<Stack gap={4}>
				<div ref={divRef} />
				{status === "Ongoing" && (
					<ButtonGroup>
						<Button type="button" variant="secondary" onPress={onCancel}>
							{t(texts.common.abort)}
						</Button>
					</ButtonGroup>
				)}
			</Stack>
			<ClcPaymentSuccessModal
				isOpen={isPaymentSuccessModalOpen}
				closeModal={() => setIsPaymentSuccessModalOpen(false)}
			/>
		</>
	);
};

export default ClcPayment;
